import { Component } from "react";
import { FormattedMessage, injectIntl } from "react-intl";

import PropTypes from "prop-types";
import { Form, FormSelect, Image } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import FormControl from "react-bootstrap/FormControl";
import FormGroup from "react-bootstrap/FormGroup";
import FormLabel from "react-bootstrap/FormLabel";
import Modal from "react-bootstrap/Modal";
import { PREVIEW_IMAGE_DATA, QUESTION_ACTION_FIELD, QUESTION_RESULT } from "../../../../ducks/questionLibrary";
import ModalDialogUtil from "../../../../utils/ModalDialogUtil";
import ObjectUtils from "../../../../utils/ObjectUtils";
import { cleanText } from "../../../../utils/QuestionUtils";
import QuestionStructureSelectContainer from "./QuestionStructureSelectContainer";

/**
 *Component which represents a modal of question to library
 */
class QuestionLibraryModal extends Component {
  componentDidMount() {
    const instance = this;

    document.onpaste = function (event) {
      // use event.originalEvent.clipboard for newer chrome versions
      const items = (event.clipboardData || event.originalEvent.clipboardData).items;

      const blob = items[0].getAsFile();

      // load image if there is a pasted image
      if (blob !== null) {
        const reader = new FileReader();
        reader.onload = function (event) {
          instance.props.changeProperty(PREVIEW_IMAGE_DATA, event.target.result);
        };

        reader.readAsDataURL(blob);
      }
    };
  }

  componentWillUnmount() {
    document.onpaste = undefined;
  }

  changeQuestionName = (event) => {
    const { questionName, selectedPage, changeQuestionField, changeProperty } = this.props;
    // only in editor for preview in collapsible
    if (changeQuestionField) {
      changeQuestionField(selectedPage, questionName, "name", cleanText(event.target.value));
    }
    changeProperty(event.target.name, event.target.value);
  };

  changeProperty = (event) => {
    this.props.changeProperty(event.target.name, event.target.value);
  };

  setNewValue = (event, listOfValues) => {
    let target = ObjectUtils.findByObjectProp(listOfValues, "id", event.target.value);
    if (target.id) {
      this.props.changeProperty(event.target.name, target);
    }
  };

  onButtonClick = (name, value) => {
    const { changeProperty } = this.props;
    changeProperty(name, value);
  };

  toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  handleFileChange = async (event) => {
    event.preventDefault();

    const base64FileData = await this.toBase64(event.target.files[0]);

    this.props.changeProperty(PREVIEW_IMAGE_DATA, base64FileData);
  };

  handleAddQuestionToLibrary = () => {
    const { addQuestionToLibrary, questionSync, surveyName, surveyIdentifier, surveyVersion } = this.props;

    addQuestionToLibrary(questionSync, surveyName, surveyIdentifier, surveyVersion);
  };

  handleCancel = () => {
    const { questionName, selectedPage, removeQuestion } = this.props;
    this.onButtonClick("showModalQuestionLibrarySettings", false);
    this.onButtonClick("showModalQuestionMyLibrarySettings", false);
    removeQuestion && removeQuestion(selectedPage, questionName);
  };

  render() {
    const {
      intl,
      showModalQuestionLibrarySettings,
      questionName,
      questionActionField,
      questionResult,
      previewImageData,
      questionId,
      questionIdValid,
      questionSync,
      surveyLanguageForQuestionLibrary,
      templateLanguages,
      target
    } = this.props;
    let questionTitle = cleanText(questionName);
    return (
      <Modal size="lg" show={showModalQuestionLibrarySettings} onHide={() => ModalDialogUtil.closeModalDialogBodyFix()}>
        <Modal.Header>
          <Modal.Title>
            {target === "questionLibrary" ? (
              <FormattedMessage id="survey.editor.edit.question.library.title" />
            ) : (
              <FormattedMessage id="survey.editor.tooltip.question.library" />
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormGroup  className="mb-3">
            <FormLabel>{intl.formatMessage({ id: "question.library.set.question.name" })}</FormLabel>
            <FormControl
              name="questionName"
              type="text"
              value={questionTitle}
              onChange={(e) => this.changeQuestionName(e)}
            />
          </FormGroup>
          {questionSync && (
            <>
              <FormGroup  className="mb-3">
                <FormLabel>
                  {intl.formatMessage({ id: "question.library.set.question.id" })}*
                  {!questionIdValid && (
                    <span style={{ color: "red" }}>
                      {" "}
                      <FormattedMessage id="question.library.question.id.invalid" />
                    </span>
                  )}
                </FormLabel>
                <FormControl
                  name="questionId"
                  type="text"
                  value={questionId}
                  onChange={(e) => this.changeProperty(e)}
                />
              </FormGroup>
              <FormGroup  className="mb-3">
                <FormLabel>{intl.formatMessage({ id: "user.surveyBuilder.applicableFor" })} *</FormLabel>
                <QuestionStructureSelectContainer
                  id={"structureSelect"}
                  onActionFieldChange={(actionField) => this.props.changeProperty(QUESTION_ACTION_FIELD, actionField)}
                  onResultChange={(result) => this.props.changeProperty(QUESTION_RESULT, result)}
                  selectedActionFieldId={questionActionField ? questionActionField.id : undefined}
                  selectedResultId={questionResult ? questionResult.id : undefined}
                />
              </FormGroup>
            </>
          )}
          <FormGroup  className="mb-3">
            <FormLabel>{intl.formatMessage({ id: "app.common.Language" })}</FormLabel>
            <FormSelect
              name="surveyLanguageForQuestionLibrary"
              onChange={(event) => {
                this.setNewValue(event, templateLanguages);
              }}
              value={surveyLanguageForQuestionLibrary ? surveyLanguageForQuestionLibrary.id : "de"}
            >
              {templateLanguages &&
                templateLanguages.map((language, i) => (
                  <option key={language.name + "-" + i} value={language.id}>
                    {language.name}
                  </option>
                ))}
              ;
            </FormSelect>
          </FormGroup>
          {questionSync && (
            <FormGroup  className="mb-3">
              <FormLabel>
                <FormattedMessage id="question.library.preview.image" />
              </FormLabel>
              {previewImageData && <Image src={previewImageData} />}
              <Form.Control type="file" onChange={this.handleFileChange} accept="image/*" />
            </FormGroup>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => this.handleCancel()}>
            <FormattedMessage id="app.common.Cancel" />
          </Button>
          <Button
            onClick={() => this.handleAddQuestionToLibrary(questionSync)}
            disabled={(questionSync && !questionId) || !questionName}
          >
            <FormattedMessage id="app.common.action.save" />
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

QuestionLibraryModal.propTypes = {
  showModalQuestionLibrarySettings: PropTypes.bool,
  showModalAddQuestionFromLibrary: PropTypes.bool,
  questionName: PropTypes.string,
  questionId: PropTypes.string,
  questionIdValid: PropTypes.bool,
  questionSync: PropTypes.bool,
  surveyName: PropTypes.string,
  surveyIdentifier: PropTypes.string,
  surveyVersion: PropTypes.number,
  questionActionField: PropTypes.object,
  questionResult: PropTypes.object,
  previewImageData: PropTypes.string,
  target: PropTypes.string,
  surveyLanguageForQuestionLibrary: PropTypes.object,
  questionLibrary: PropTypes.array,
  templateLanguages: PropTypes.array,
  questionsToAdd: PropTypes.array,
  changeProperty: PropTypes.func,
  addQuestionToLibrary: PropTypes.func,
  getQuestionLibrary: PropTypes.func,
  addQuestionToTemplate: PropTypes.func,
  submitAdding: PropTypes.func
};

export default injectIntl(QuestionLibraryModal);
